import { post } from './api';
import { refreshMe } from './session';
import { toast } from './ui';

declare global {
	interface Window {
		appendHelcimPayIframe?: (checkoutToken: string, allowExit?: boolean) => void;
		removeHelcimPayIframe?: () => void;
	}
}

const HELCIM_SCRIPT = 'https://secure.helcim.app/helcim-pay/services/start.js';
let scriptLoad: Promise<void> | null = null;

function loadHelcim(): Promise<void> {
	if (window.appendHelcimPayIframe) return Promise.resolve();
	scriptLoad ??= new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.src = HELCIM_SCRIPT;
		s.onload = () => resolve();
		s.onerror = () => {
			scriptLoad = null;
			reject(new Error('Could not load the payment form.'));
		};
		document.head.appendChild(s);
	});
	return scriptLoad;
}

function closeModal() {
	try {
		window.removeHelcimPayIframe?.();
	} catch {
		document.getElementById('helcimPayIframe')?.remove();
	}
}

/**
 * Start a purchase. With Helcim, the card form opens in Helcim's own modal; its result goes to the
 * server, which verifies it with Helcim before granting anything. Resolves true when paid.
 */
export async function startCheckout(body: Record<string, unknown>): Promise<boolean> {
	const r = await post('/api/billing/checkout', body);
	if (r.provider === 'simulate' || r.url) {
		location.href = r.url;
		return false;
	}
	await loadHelcim();
	return new Promise<boolean>((resolve) => {
		const key = `helcim-pay-js-${r.checkoutToken}`;
		const onMessage = async (event: MessageEvent) => {
			const d = event.data;
			if (!d || d.eventName !== key) return;
			if (d.eventStatus === 'ABORTED') {
				toast('The payment was declined. You can try another card.', 'warn', 7000);
				return;
			}
			if (d.eventStatus === 'HIDE') {
				window.removeEventListener('message', onMessage);
				closeModal();
				resolve(false);
				return;
			}
			if (d.eventStatus === 'SUCCESS') {
				window.removeEventListener('message', onMessage);
				closeModal();
				try {
					await post('/api/billing/helcim/confirm', { checkoutId: r.checkoutId, response: d.eventMessage });
					await refreshMe().catch(() => undefined);
					toast('Payment complete. Thank you!', 'ok', 6000);
					resolve(true);
				} catch (e) {
					toast(`Payment received but not confirmed yet: ${(e as Error).message}. It will be applied automatically once Helcim notifies us.`, 'warn', 10000);
					resolve(false);
				}
			}
		};
		window.addEventListener('message', onMessage);
		window.appendHelcimPayIframe!(r.checkoutToken, true);
	});
}
